import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Input,
  Badge,
  Skeleton,
  Label,
  SearchDropdownMultiSelect,
  Toastify,
} from 'lift-ui';
import { Search, Settings2, RefreshCw } from 'lucide-react';
import OverviewConfigEditor from './components/OverviewConfigEditor';
import {
  listMasterSkills,
  listBrandSkills,
  listUserSkills,
  listBrands,
  listUsers,
  type OverviewScope,
  type OverviewOwnerRef,
  type MasterSkillListItem,
  type BrandOption,
  type UserOption,
} from './api';

/** A buddy row in the list. Fork rows also carry the owner they belong to so
 *  the editor can address the right per-brand / per-user config. */
interface BuddyRow {
  ownerId: string; // master skill id OR fork copy_id
  slug: string;
  displayTitle: string;
  description?: string | null;
  ownerLabel?: string; // brand/user name (fork scopes, when >1 owner loaded)
  brandId?: number;
  userId?: number;
}

interface EditorTarget {
  ref: OverviewOwnerRef;
  buddyName: string;
  buddySlug?: string;
}

const BuddyConfigTable: React.FC<{
  rows: BuddyRow[];
  loading: boolean;
  emptyMessage: string;
  ownerColumn?: string | null;
  onEdit: (row: BuddyRow) => void;
}> = ({ rows, loading, emptyMessage, ownerColumn, onEdit }) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-2 mt-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="mt-8 text-center text-sm text-gray-500">{emptyMessage}</div>
    );
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Buddy</TableHead>
            {ownerColumn && <TableHead>{ownerColumn}</TableHead>}
            <TableHead>Slug</TableHead>
            <TableHead className="w-40 text-right">Config</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={`${row.ownerLabel ?? ''}-${row.ownerId}`}
              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            >
              <TableCell>
                <div className="font-medium text-gray-900">{row.displayTitle}</div>
                {row.description && (
                  <div className="text-xs text-gray-500 line-clamp-1 max-w-md">
                    {row.description}
                  </div>
                )}
              </TableCell>
              {ownerColumn && (
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {row.ownerLabel}
                  </Badge>
                </TableCell>
              )}
              <TableCell>
                <span className="inline-block px-1.5 py-0.5 text-xs rounded border border-gray-200 bg-gray-50 text-gray-700 font-mono">
                  {row.slug}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(row)}
                  className="inline-flex items-center gap-2 h-8 px-3"
                >
                  <Settings2 className="h-4 w-4" />
                  Edit config
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const BuddyConfig: React.FC = () => {
  const [scope, setScope] = useState<OverviewScope>('master');
  const [search, setSearch] = useState('');

  // Master scope data
  const [masterRows, setMasterRows] = useState<MasterSkillListItem[]>([]);
  const [masterLoading, setMasterLoading] = useState(false);

  // Owner option lists (for the pickers)
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);

  // Selected owners
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Loaded fork rows (buddies across the selected owners)
  const [forkRows, setForkRows] = useState<BuddyRow[]>([]);
  const [forkLoading, setForkLoading] = useState(false);

  // Editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);

  const loadMaster = useCallback(async () => {
    setMasterLoading(true);
    try {
      const res = await listMasterSkills();
      setMasterRows(res.data || []);
    } catch (err: any) {
      Toastify('error', err?.response?.data?.detail || 'Failed to load buddies');
    } finally {
      setMasterLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMaster();
  }, [loadMaster]);

  // Lazily load the owner option lists the first time a fork tab is opened.
  const ensureOwnersLoaded = useCallback(async () => {
    if (brands.length || users.length || ownersLoading) return;
    setOwnersLoading(true);
    try {
      const [brandList, userList] = await Promise.all([listBrands(), listUsers()]);
      setBrands(brandList);
      setUsers(userList);
    } catch (err: any) {
      Toastify(
        'error',
        err?.response?.data?.detail || 'Failed to load brand/user lists'
      );
    } finally {
      setOwnersLoading(false);
    }
  }, [brands.length, users.length, ownersLoading]);

  const brandItems = useMemo(
    () => brands.map((b) => ({ id: String(b.id), label: b.name })),
    [brands]
  );
  const userItems = useMemo(
    () =>
      users.map((u) => ({
        id: String(u.id),
        label: u.email ? `${u.name} (${u.email})` : u.name,
      })),
    [users]
  );

  const loadForks = useCallback(async () => {
    const selectedIds = scope === 'brand' ? selectedBrandIds : selectedUserIds;
    if (selectedIds.length === 0) {
      setForkRows([]);
      return;
    }
    setForkLoading(true);
    try {
      const results = await Promise.all(
        selectedIds.map(async (id) => {
          const copies =
            scope === 'brand'
              ? await listBrandSkills(id)
              : await listUserSkills(id);
          const ownerLabel =
            scope === 'brand'
              ? brands.find((b) => String(b.id) === id)?.name ?? `Brand ${id}`
              : users.find((u) => String(u.id) === id)?.name ?? `User ${id}`;
          return copies.map<BuddyRow>((c) => ({
            ownerId: c.id,
            slug: c.slug,
            displayTitle: c.display_title,
            description: c.description,
            ownerLabel,
            brandId: scope === 'brand' ? Number(id) : undefined,
            userId: scope === 'user' ? Number(id) : undefined,
          }));
        })
      );
      setForkRows(results.flat());
    } catch (err: any) {
      Toastify(
        'error',
        err?.response?.data?.detail || `Failed to load buddies for ${scope}s`
      );
    } finally {
      setForkLoading(false);
    }
  }, [scope, selectedBrandIds, selectedUserIds, brands, users]);

  useEffect(() => {
    if (scope === 'master') return;
    loadForks();
  }, [scope, loadForks]);

  const currentRows: BuddyRow[] = useMemo(() => {
    const source: BuddyRow[] =
      scope === 'master'
        ? masterRows.map((s) => ({
            ownerId: s.id,
            slug: s.slug,
            displayTitle: s.display_title,
            description: s.description,
          }))
        : forkRows;

    const q = search.trim().toLowerCase();
    if (!q) return source;
    return source.filter(
      (r) =>
        r.displayTitle.toLowerCase().includes(q) ||
        r.slug.toLowerCase().includes(q) ||
        (r.ownerLabel?.toLowerCase().includes(q) ?? false)
    );
  }, [scope, masterRows, forkRows, search]);

  const openEditor = (row: BuddyRow) => {
    let ref: OverviewOwnerRef;
    if (scope === 'master') {
      ref = { scope: 'master', ownerId: row.ownerId };
    } else if (scope === 'brand') {
      ref = { scope: 'brand', ownerId: row.ownerId, brandId: row.brandId };
    } else {
      ref = { scope: 'user', ownerId: row.ownerId, userId: row.userId };
    }
    setEditorTarget({ ref, buddyName: row.displayTitle, buddySlug: row.slug });
    setEditorOpen(true);
  };

  const handleScopeChange = (value: string) => {
    const next = value as OverviewScope;
    setScope(next);
    setSearch('');
    if (next !== 'master') ensureOwnersLoaded();
  };

  const handleRefresh = useCallback(() => {
    if (scope === 'master') {
      loadMaster();
    } else {
      loadForks();
    }
  }, [scope, loadMaster, loadForks]);

  const multipleOwners =
    new Set(forkRows.map((r) => r.ownerLabel)).size > 1;

  const renderForkPicker = (kind: 'brand' | 'user') => {
    const items = kind === 'brand' ? brandItems : userItems;
    const selected = kind === 'brand' ? selectedBrandIds : selectedUserIds;
    const setSelected = kind === 'brand' ? setSelectedBrandIds : setSelectedUserIds;
    return (
      <div className="mt-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-medium text-gray-600 capitalize">
            {kind}s
          </Label>
          <div className="w-80">
            <SearchDropdownMultiSelect
              items={items}
              selectedItems={selected}
              onSelectionChange={setSelected}
              placeholder={`Search ${kind}s...`}
              triggerLabel={
                selected.length
                  ? `${selected.length} ${kind}${selected.length > 1 ? 's' : ''} selected`
                  : ownersLoading
                  ? 'Loading…'
                  : `Select ${kind}s`
              }
              disabled={ownersLoading}
              fullWidth
            />
          </div>
        </div>
      </div>
    );
  };

  const renderForkContent = (kind: 'brand' | 'user') => {
    const selected = kind === 'brand' ? selectedBrandIds : selectedUserIds;
    return (
      <>
        {renderForkPicker(kind)}
        {selected.length === 0 ? (
          <div className="mt-8 text-center text-sm text-gray-400">
            Select {kind}s to view their buddies.
          </div>
        ) : (
          <BuddyConfigTable
            rows={currentRows}
            loading={forkLoading}
            ownerColumn={multipleOwners ? (kind === 'brand' ? 'Brand' : 'User') : null}
            emptyMessage={`No buddies assigned to the selected ${kind}${
              selected.length > 1 ? 's' : ''
            }.`}
            onEdit={openEditor}
          />
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      <PageHeader
        title="Buddy Configuration"
        renderCustomTop={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search buddies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-80"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={masterLoading || forkLoading}
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${masterLoading || forkLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto px-8 pt-6 pb-8">
        <Tabs value={scope} onValueChange={handleScopeChange}>
          <TabsList className="grid grid-cols-3 w-fit">
            <TabsTrigger value="master">Master</TabsTrigger>
            <TabsTrigger value="brand">Brand</TabsTrigger>
            <TabsTrigger value="user">User</TabsTrigger>
          </TabsList>

          <TabsContent value="master" className="mt-4">
            <p className="text-sm text-gray-500">
              Edit the base config for each master buddy. This is inherited by
              brand and user forks when they are assigned.
            </p>
            <BuddyConfigTable
              rows={currentRows}
              loading={masterLoading}
              emptyMessage="No buddies found."
              onEdit={openEditor}
            />
          </TabsContent>

          <TabsContent value="brand" className="mt-4">
            <p className="text-sm text-gray-500">
              Edit per-brand overrides of a buddy&apos;s config. Pick one or more
              brands to view their assigned buddies.
            </p>
            {renderForkContent('brand')}
          </TabsContent>

          <TabsContent value="user" className="mt-4">
            <p className="text-sm text-gray-500">
              Edit per-user overrides of a buddy&apos;s config. Pick one or more
              users to view their assigned buddies.
            </p>
            {renderForkContent('user')}
          </TabsContent>
        </Tabs>
      </div>

      <OverviewConfigEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        ownerRef={editorTarget?.ref ?? null}
        buddyName={editorTarget?.buddyName ?? ''}
        buddySlug={editorTarget?.buddySlug}
      />
    </div>
  );
};

export default BuddyConfig;
